import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { MapPin, Star, Search, Filter, Grid, List } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VenuesPage = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [indoorOnly, setIndoorOnly] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const params = new URLSearchParams();
        if (indoorOnly) params.append('indoor', 'true');
        
        const response = await fetch(`${API}/venues?${params}`);
        if (response.ok) {
          const data = await response.json();
          setVenues(data);
        }
      } catch (error) {
        console.error('Error fetching venues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, [indoorOnly]);

  const filteredVenues = venues
    .filter(venue => 
      venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      venue.city.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating': return b.rating - a.rating;
        case 'price-low': return a.price_per_hour - b.price_per_hour;
        case 'price-high': return b.price_per_hour - a.price_per_hour;
        default: return 0;
      }
    });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="venues-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Venues</h1>
          <p className="text-muted-foreground">Find and book the perfect court for your game</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search venues or cities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="venue-search"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Switch 
                id="indoor" 
                checked={indoorOnly} 
                onCheckedChange={setIndoorOnly}
                data-testid="indoor-filter"
              />
              <Label htmlFor="indoor" className="text-sm">Indoor only</Label>
            </div>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="price-low">Price: Low</SelectItem>
                <SelectItem value="price-high">Price: High</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden sm:flex border rounded-lg">
              <Button 
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="icon"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Venues Grid/List */}
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-muted animate-pulse rounded-xl"></div>
            ))}
          </div>
        ) : filteredVenues.length > 0 ? (
          <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
            {filteredVenues.map((venue) => (
              <Card 
                key={venue.venue_id} 
                className={`card-interactive overflow-hidden group cursor-pointer ${viewMode === 'list' ? 'flex' : ''}`}
                onClick={() => navigate(`/venues/${venue.venue_id}`)}
                data-testid={`venue-card-${venue.venue_id}`}
              >
                <div className={`relative ${viewMode === 'list' ? 'w-48 flex-shrink-0' : 'h-48'} overflow-hidden`}>
                  <img 
                    src={venue.photos?.[0] || 'https://images.pexels.com/photos/14262635/pexels-photo-14262635.jpeg'} 
                    alt={venue.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={venue.indoor ? 'bg-primary text-white' : 'bg-secondary text-secondary-foreground'}>
                      {venue.indoor ? 'Indoor' : 'Outdoor'}
                    </Badge>
                  </div>
                  <div className="absolute inset-0 venue-card-overlay"></div>
                </div>
                <CardContent className={`p-5 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <h3 className="font-heading font-semibold text-lg mb-1">{venue.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center mb-2">
                    <MapPin className="h-4 w-4 mr-1" />
                    {venue.city}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{venue.description}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {venue.amenities?.slice(0, 3).map((amenity, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{amenity}</Badge>
                    ))}
                    {venue.amenities?.length > 3 && (
                      <Badge variant="outline" className="text-xs">+{venue.amenities.length - 3}</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-500 mr-1" />
                      <span className="font-medium">{venue.rating}</span>
                      <span className="text-muted-foreground text-sm ml-1">({venue.review_count})</span>
                    </div>
                    <div className="text-right">
                      <span className="font-heading font-bold text-primary">₱{venue.price_per_hour}</span>
                      <span className="text-sm text-muted-foreground">/hr</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">No venues found</h3>
            <p className="text-muted-foreground">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VenuesPage;
